import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, Package, Clock, Calendar } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_MAP = {
  pending: { label: 'Pendiente', bg: 'bg-amber-50', text: 'text-amber-700', dot: 'bg-amber-400' },
  confirmed: { label: 'Confirmado', bg: 'bg-blue-50', text: 'text-blue-700', dot: 'bg-blue-400' },
  delivered: { label: 'Entregado', bg: 'bg-emerald-50', text: 'text-emerald-700', dot: 'bg-emerald-400' },
  cancelled: { label: 'Cancelado', bg: 'bg-red-50', text: 'text-red-700', dot: 'bg-red-400' },
};

const ClientOrdersPage = () => {
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API}/store/my-orders`).then(res => setOrders(res.data)).catch(() => {}).finally(() => setLoading(false));
  }, []);

  return (
    <div className="max-w-md mx-auto space-y-5 animate-fade-in pb-8" data-testid="client-orders-page">
      <button onClick={() => navigate('/client/supplements')} className="flex items-center gap-2 text-sm text-gray-500 hover:text-gray-700 transition-colors" data-testid="back-to-supplements">
        <ArrowLeft size={16} /> Suplementos
      </button>

      <div className="pt-1">
        <h1 className="text-3xl font-bold text-gray-900" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
          Tus Pedidos
        </h1>
        <p className="text-sm text-gray-400 mt-1 font-medium">{orders.length} orden{orders.length !== 1 ? 'es' : ''} realizada{orders.length !== 1 ? 's' : ''}</p>
      </div>

      {loading ? (
        <div className="flex justify-center py-12">
          <div className="w-10 h-10 border-4 border-indigo-100 border-t-indigo-500 rounded-full animate-spin" />
        </div>
      ) : orders.length === 0 ? (
        <div className="bg-white rounded-3xl border border-gray-100/50 shadow-sm p-10 text-center" data-testid="no-orders">
          <div className="w-16 h-16 bg-gradient-to-br from-indigo-50 to-violet-50 rounded-2xl flex items-center justify-center mx-auto mb-4">
            <Package size={32} className="text-indigo-300" />
          </div>
          <p className="font-bold text-gray-700" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>Sin pedidos</p>
          <p className="text-sm text-gray-400 mt-1">Tus ordenes apareceran aqui</p>
        </div>
      ) : (
        <div className="space-y-3">
          {orders.map((order) => {
            const status = STATUS_MAP[order.status] || STATUS_MAP.pending;
            return (
              <div key={order.id} className="bg-white rounded-2xl border border-gray-100/50 overflow-hidden shadow-sm" data-testid={`order-card-${order.id}`}>
                <div className="h-1 bg-gradient-to-r from-indigo-400 via-violet-500 to-purple-500" />
                <div className="p-4">
                  <div className="flex items-start justify-between gap-3 mb-3">
                    <div>
                      <p className="font-bold text-gray-900" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
                        {order.product_name}
                      </p>
                      <div className="flex items-center gap-1.5 mt-1">
                        <Calendar size={12} className="text-gray-400" />
                        <span className="text-xs text-gray-500 font-medium">
                          {new Date(order.created_at).toLocaleDateString('es-DO', { day: 'numeric', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                        </span>
                      </div>
                    </div>
                    <div className={`flex items-center gap-1.5 px-2.5 py-1 rounded-full ${status.bg}`}>
                      <span className={`w-1.5 h-1.5 rounded-full ${status.dot}`} />
                      <span className={`text-[11px] font-bold ${status.text}`}>{status.label}</span>
                    </div>
                  </div>
                  <div className="flex items-center justify-between bg-gray-50 rounded-xl px-3.5 py-2.5">
                    <div className="flex items-center gap-4">
                      <div>
                        <p className="text-[10px] text-gray-400 font-semibold uppercase">Cantidad</p>
                        <p className="text-sm font-bold text-gray-900">{order.quantity}</p>
                      </div>
                      <div>
                        <p className="text-[10px] text-gray-400 font-semibold uppercase">Precio Und.</p>
                        <p className="text-sm font-bold text-gray-900">RD${order.unit_price?.toLocaleString()}</p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="text-[10px] text-gray-400 font-semibold uppercase">Total</p>
                      <p className="text-lg font-black text-gray-900" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
                        RD${order.total?.toLocaleString()}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default ClientOrdersPage;
